import { ChangeEvent, useCallback } from 'react'
import styles from './RadioButtonGroup.module.scss'

export type RadioOption<T> = {
  label: string
  value: T
}

type Props<T = string> = {
  className?: string
  legend?: string
  onSelect: (value: T) => void
  options: Array<RadioOption<T>>
  selectedOption?: T
  testPrefix?: string
  idSuffix?: string
  disabled?: boolean
}

const RadioButtonGroup = <T,>({
  className = styles.default,
  legend = '',
  onSelect,
  options,
  selectedOption,
  testPrefix = '',
  idSuffix = '',
  disabled = false
}: Props<T>) => {
  const onRadiobuttonChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const option = options[Number(e.target.value)]
      if (option) onSelect(option.value)
    },
    [onSelect, options]
  )

  const dash = testPrefix ? '-' : ''
  const testid = `${testPrefix}${dash}${legend?.replace(/ /g, '-')}`
  const simpleLegend = legend?.replace(':', '') ?? ''

  const addToId = idSuffix ? '-' + idSuffix : ''

  return (
    <fieldset className={`${className}`} data-testid={testid}>
      {legend && <legend>{legend}</legend>}
      {options.map((option, i) => {
        const labelWithDashes = option.label.replace(/ /g, '-')
        const optionTestId = `${testPrefix}${dash}${labelWithDashes}`
        const isSelected = selectedOption === option.value
        const optionTitle =
          option.label.length > 1 ? `${simpleLegend} ${option.label}` : ''
        return (
          <label
            title={optionTitle}
            data-testid={optionTestId}
            key={optionTestId}
            data-is-selected={isSelected}
          >
            <input
              type="radio"
              value={i}
              onChange={onRadiobuttonChange}
              checked={isSelected}
              name={testid}
              id={labelWithDashes + addToId}
              disabled={disabled}
            />
            <span>{option.label}</span>
          </label>
        )
      })}
    </fieldset>
  )
}

export default RadioButtonGroup
